import React, { useEffect, useMemo, useState } from 'react';
import { Card, Button, Table, Space, Form, Input, InputNumber, DatePicker, Select, Modal, Tag, Row, Col, Statistic, Upload, message, Popconfirm, Divider, Image, Grid, Tabs, Drawer, List } from 'antd';
import { PlusOutlined, UploadOutlined, ReloadOutlined, FilterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../api/http';
import { useAuth } from '../../context/AuthContext';
import { fetchBlobUrl, openWithAuth } from '../../utils/authedResource';

const { RangePicker } = DatePicker;

const isImageFile = (file) => {
  const name = String(file?.name || file?.file_name || '').toLowerCase();
  const url = String(file?.url || '').toLowerCase();
  return /(\.png|\.jpg|\.jpeg|\.gif|\.webp|\.bmp|\.svg)(\?|#|$)/.test(name || url) || String(file?.type || '').startsWith('image/');
};

const TYPE_OPTIONS = [
  { label: '全额报销', value: 'full', color: 'green' },
  { label: '部分报销', value: 'partial', color: 'orange' }
];

const STATUS_OPTIONS = [
  { label: '待处理', value: 'pending', color: 'default' },
  { label: '已报销', value: 'paid', color: 'blue' },
  { label: '已拒绝', value: 'rejected', color: 'red' }
];

const getDisplayedReimbursedAmount = (record) => {
  const approved = Number(record?.approved_amount || 0);
  if (approved > 0) return approved;
  return Number(record?.reimbursed_amount || 0);
};

const ReimbursementPage = () => {
  const { user, hasPermission } = useAuth();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const canManage = hasPermission?.(['reimbursement_manage']);
  const canView = hasPermission?.(['reimbursement_view', 'reimbursement_manage']);
  const canApprove = hasPermission?.(['project_manager']);
  const showApproveTab = !!(canApprove || canManage);

  const [form] = Form.useForm();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [billFiles, setBillFiles] = useState([]);
  const [invoiceFiles, setInvoiceFiles] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  const [activeTab, setActiveTab] = useState('mine');
  const [payForm] = Form.useForm();
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [paySaving, setPaySaving] = useState(false);
  const [payTarget, setPayTarget] = useState(null);

  const [paymentsDrawerOpen, setPaymentsDrawerOpen] = useState(false);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [paymentsTarget, setPaymentsTarget] = useState(null);

  const [pendingLoading, setPendingLoading] = useState(false);
  const [pending, setPending] = useState([]);
  const [pendingPagination, setPendingPagination] = useState({ current: 1, pageSize: 30, total: 0 });
  const [approvedLoading, setApprovedLoading] = useState(false);
  const [approved, setApproved] = useState([]);
  const [approvedPagination, setApprovedPagination] = useState({ current: 1, pageSize: 30, total: 0 });
  const [approveSubTab, setApproveSubTab] = useState('pending');
  const [approveModal, setApproveModal] = useState({ open: false, record: null, saving: false });
  const [rejectModal, setRejectModal] = useState({ open: false, record: null, saving: false });
  const [approveForm] = Form.useForm();
  const [rejectForm] = Form.useForm();

  const [payInForm] = Form.useForm();
  const [payInModal, setPayInModal] = useState({ open: false, record: null, saving: false });

  const [filters, setFilters] = useState({
    q: '',
    status: undefined,
    type: undefined,
    dateRange: []
  });

  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });

  const isOwner = (record) => Number(record?.user_id) === Number(user?.id);
  const canEditRecord = (record) => canManage || isOwner(record);

  const loadSummary = async (nextFilters = filters) => {
    if (!canView) return;
    try {
      const params = {
        mine: 1,
        q: nextFilters.q || undefined,
        status: nextFilters.status || undefined,
        type: nextFilters.type || undefined
      };
      if (Array.isArray(nextFilters.dateRange) && nextFilters.dateRange.length === 2) {
        params.start_date = nextFilters.dateRange[0].format('YYYY-MM-DD');
        params.end_date = nextFilters.dateRange[1].format('YYYY-MM-DD');
      }
      const res = await api.get('/reimbursements/summary', { params });
      if (res.data?.code === 0) setSummary(res.data.data || {});
    } catch (e) {
      // ignore summary errors
    }
  };

  const loadRecords = async (page = pagination.current, pageSize = pagination.pageSize, nextFilters = filters) => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = {
        mine: 1,
        page,
        pageSize,
        q: nextFilters.q || undefined,
        status: nextFilters.status || undefined,
        type: nextFilters.type || undefined
      };
      if (Array.isArray(nextFilters.dateRange) && nextFilters.dateRange.length === 2) {
        params.start_date = nextFilters.dateRange[0].format('YYYY-MM-DD');
        params.end_date = nextFilters.dateRange[1].format('YYYY-MM-DD');
      }
      const res = await api.get('/reimbursements', { params });
      if (res.data?.code === 0) {
        setRecords(res.data.data || []);
        setPagination((prev) => ({ ...prev, current: page, pageSize, total: res.data.total || 0 }));
      }
    } catch (e) {
      message.error('加载报销记录失败');
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async (reimbursementId) => {
    setPaymentsLoading(true);
    try {
      const res = await api.get(`/reimbursements/${reimbursementId}/payments`);
      if (res.data?.code === 0) setPayments(res.data.data || []);
    } catch (e) {
      message.error('加载分次记录失败');
    } finally {
      setPaymentsLoading(false);
    }
  };

  const loadPending = async (page = pendingPagination.current, pageSize = pendingPagination.pageSize) => {
    if (!canApprove) return;
    setPendingLoading(true);
    try {
      const res = await api.get('/reimbursements/payments/pending', { params: { page, pageSize } });
      if (res.data?.code === 0) {
        setPending(res.data.data || []);
        setPendingPagination((prev) => ({ ...prev, current: page, pageSize, total: res.data.total || 0 }));
      }
    } catch (e) {
      message.error('加载待审批列表失败');
    } finally {
      setPendingLoading(false);
    }
  };

  const loadApprovedMissingDocs = async (page = approvedPagination.current, pageSize = approvedPagination.pageSize) => {
    if (!canApprove) return;
    setApprovedLoading(true);
    try {
      const res = await api.get('/reimbursements/payments/approved', { params: { page, pageSize, missing_doc: 1 } });
      if (res.data?.code === 0) {
        setApproved(res.data.data || []);
        setApprovedPagination((prev) => ({ ...prev, current: page, pageSize, total: res.data.total || 0 }));
      }
    } catch (e) {
      // ignore
    } finally {
      setApprovedLoading(false);
    }
  };

  useEffect(() => {
    loadRecords(1, pagination.pageSize, filters);
    loadSummary(filters);
  }, []);

  useEffect(() => {
    if (activeTab === 'approve' && canApprove) {
      if (approveSubTab === 'pending') {
        loadPending(1, pendingPagination.pageSize);
      } else if (approveSubTab === 'approved_missing') {
        loadApprovedMissingDocs(1, approvedPagination.pageSize);
      }
    }
  }, [activeTab, canApprove, approveSubTab]);

  const handleSearch = () => {
    loadRecords(1, pagination.pageSize, filters);
    loadSummary(filters);
  };

  const handleReset = () => {
    const reset = { q: '', status: undefined, type: undefined, dateRange: [] };
    setFilters(reset);
    loadRecords(1, pagination.pageSize, reset);
    loadSummary(reset);
  };

  const openCreate = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({
      reimbursement_type: 'full',
      status: 'pending',
      total_amount: 0,
      reimbursed_amount: 0
    });
    setBillFiles([]);
    setInvoiceFiles([]);
    setModalOpen(true);
  };

  const fetchAttachments = async (recordId) => {
    try {
      const res = await api.get(`/reimbursements/${recordId}/attachments`);
      if (res.data?.code === 0) {
        const list = res.data.data || [];
        const billList = list.filter((item) => item.file_type === 'bill').map((item) => ({
          uid: String(item.id),
          name: item.file_name,
          status: 'done',
          url: item.url,
          attachmentId: item.id
        }));
        const invoiceList = list.filter((item) => item.file_type === 'invoice').map((item) => ({
          uid: String(item.id),
          name: item.file_name,
          status: 'done',
          url: item.url,
          attachmentId: item.id
        }));
        setBillFiles(billList);
        setInvoiceFiles(invoiceList);
      }
    } catch (e) {
      // ignore
    }
  };

  const openEdit = async (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      title: record.title,
      description: record.description || '',
      expense_date: record.expense_date ? dayjs(record.expense_date) : null,
      total_amount: record.total_amount,
      reimbursed_amount: record.reimbursed_amount,
      reimbursement_type: record.reimbursement_type,
      status: record.status,
      remark: record.remark || ''
    });
    setModalOpen(true);
    await fetchAttachments(record.id);
  };

  const openPayModal = (record) => {
    setPayTarget(record);
    payForm.resetFields();
    const remaining = Number(record?.remaining_amount || 0);
    payForm.setFieldsValue({ requested_amount: remaining > 0 ? remaining : 0, request_note: '' });
    setPayModalOpen(true);
  };

  const submitPayRequest = async () => {
    try {
      const values = await payForm.validateFields();
      if (!payTarget?.id) return;
      setPaySaving(true);
      await api.post(`/reimbursements/${payTarget.id}/payments`, {
        requested_amount: values.requested_amount,
        request_note: values.request_note || ''
      });
      message.success('已提交分次报销申请，等待项目经理审批');
      setPayModalOpen(false);
      setPayTarget(null);
      loadRecords(1, pagination.pageSize, filters);
      loadSummary(filters);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || '提交失败');
    } finally {
      setPaySaving(false);
    }
  };

  const openPaymentsDrawer = async (record) => {
    setPaymentsTarget(record);
    setPaymentsDrawerOpen(true);
    await loadPayments(record.id);
  };

  const cancelPayment = async (paymentId) => {
    try {
      await api.post(`/reimbursements/payments/${paymentId}/cancel`);
      message.success('已撤销');
      if (paymentsTarget?.id) loadPayments(paymentsTarget.id);
      loadRecords(1, pagination.pageSize, filters);
      loadSummary(filters);
    } catch (e) {
      message.error(e?.response?.data?.message || '撤销失败');
    }
  };

  const openApprove = (item) => {
    setApproveModal({ open: true, record: item, saving: false });
    approveForm.resetFields();
    approveForm.setFieldsValue({ approved_amount: item.requested_amount, approve_note: '' });
  };
  const doApprove = async () => {
    try {
      const values = await approveForm.validateFields();
      const id = approveModal.record?.id;
      if (!id) return;
      setApproveModal((s) => ({ ...s, saving: true }));
      await api.post(`/reimbursements/payments/${id}/approve`, {
        approved_amount: values.approved_amount,
        approve_note: values.approve_note || ''
      });
      message.success('已审批通过');
      setApproveModal({ open: false, record: null, saving: false });
      loadPending(pendingPagination.current, pendingPagination.pageSize);
      loadRecords(1, pagination.pageSize, filters);
      loadSummary(filters);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || '审批失败');
    } finally {
      setApproveModal((s) => ({ ...s, saving: false }));
    }
  };

  const openReject = (item) => {
    setRejectModal({ open: true, record: item, saving: false });
    rejectForm.resetFields();
  };
  const doReject = async () => {
    try {
      const values = await rejectForm.validateFields();
      const id = rejectModal.record?.id;
      if (!id) return;
      setRejectModal((s) => ({ ...s, saving: true }));
      await api.post(`/reimbursements/payments/${id}/reject`, { reject_reason: values.reject_reason });
      message.success('已驳回');
      setRejectModal({ open: false, record: null, saving: false });
      loadPending(pendingPagination.current, pendingPagination.pageSize);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || '驳回失败');
    } finally {
      setRejectModal((s) => ({ ...s, saving: false }));
    }
  };

  const uploadApprovalDoc = (paymentId, afterSuccess) => async ({ file, onSuccess, onError }) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/reimbursements/payments/${paymentId}/approval-doc`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data?.code === 0) {
        message.success('审批单已上传');
        await afterSuccess?.();
        onSuccess?.(res.data.data, file);
      } else {
        onError?.(new Error('upload failed'));
      }
    } catch (e) {
      message.error(e?.response?.data?.message || '上传失败');
      onError?.(e);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const reimbursedAmount = Number(form.getFieldValue('reimbursed_amount') || 0);
      const payload = {
        title: values.title,
        description: values.description || '',
        expense_date: values.expense_date ? values.expense_date.format('YYYY-MM-DD') : null,
        total_amount: values.total_amount,
        reimbursed_amount: reimbursedAmount,
        reimbursement_type: values.reimbursement_type,
        status: values.status,
        remark: values.remark || ''
      };

      if (editingRecord?.id) {
        await api.put(`/reimbursements/${editingRecord.id}`, payload);
        message.success('报销记录已更新');
      } else {
        const res = await api.post('/reimbursements', payload);
        if (res.data?.code === 0) {
          const newId = res.data?.id;
          const localBills = (billFiles || []).filter((f) => !f?.attachmentId && f?.originFileObj);
          const localInvoices = (invoiceFiles || []).filter((f) => !f?.attachmentId && f?.originFileObj);

          if (newId && (localBills.length || localInvoices.length)) {
            try {
              const uploadedBills = [];
              for (const f of localBills) {
                const uploaded = await uploadAttachmentForRecord(newId, 'bill', f);
                if (uploaded) uploadedBills.push(uploaded);
              }
              const uploadedInvoices = [];
              for (const f of localInvoices) {
                const uploaded = await uploadAttachmentForRecord(newId, 'invoice', f);
                if (uploaded) uploadedInvoices.push(uploaded);
              }
              setBillFiles(uploadedBills);
              setInvoiceFiles(uploadedInvoices);
              message.success('报销记录已创建，附件已上传');
            } catch (e) {
              // 保持弹窗打开并切换为“编辑态”，方便继续补传
              setEditingRecord({ id: newId, ...payload });
              await fetchAttachments(newId);
              message.warning('报销记录已创建，但附件上传失败/不完整，可继续补传');
              return;
            }
          } else {
            message.success('报销记录已创建');
          }
        }
      }
      setModalOpen(false);
      setEditingRecord(null);
      loadRecords(1, pagination.pageSize, filters);
      loadSummary(filters);
    } catch (e) {
      if (e?.errorFields) return;
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const openPayIn = (record) => {
    setPayInModal({ open: true, record, saving: false });
    payInForm.setFieldsValue({ amount: undefined });
  };

  const submitPayIn = async () => {
    try {
      const values = await payInForm.validateFields();
      const record = payInModal.record;
      if (!record?.id) return;

      const total = Number(record.total_amount || 0);
      const current = Number(record.reimbursed_amount || 0);
      const delta = Number(values.amount || 0);
      if (!(delta > 0)) return;

      const next = Math.min(current + delta, total);
      const type = next >= total ? 'full' : 'partial';
      const status = next >= total ? 'paid' : 'pending';

      setPayInModal((s) => ({ ...s, saving: true }));
      await api.put(`/reimbursements/${record.id}`, {
        title: record.title,
        description: record.description || '',
        expense_date: record.expense_date ? String(record.expense_date).slice(0, 10) : null,
        total_amount: total,
        reimbursed_amount: next,
        reimbursement_type: type,
        status,
        remark: record.remark || ''
      });

      message.success('到账已记录');
      setPayInModal({ open: false, record: null, saving: false });
      loadRecords(1, pagination.pageSize, filters);
      loadSummary(filters);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || '到账失败');
    } finally {
      setPayInModal((s) => ({ ...s, saving: false }));
    }
  };

  const handleDelete = async (record) => {
    try {
      await api.delete(`/reimbursements/${record.id}`);
      message.success('已删除');
      loadRecords(1, pagination.pageSize, filters);
      loadSummary(filters);
    } catch (e) {
      message.error('删除失败');
    }
  };

  const uploadAttachment = (type) => async ({ file, onSuccess, onError }) => {
    if (!editingRecord?.id) {
      message.warning('请先保存报销记录后再上传附件');
      onError?.(new Error('missing record'));
      return;
    }
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/reimbursements/${editingRecord.id}/attachments?type=${type}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data?.code === 0) {
        const item = res.data.data;
        const nextItem = {
          uid: String(item.id),
          name: item.file_name,
          status: 'done',
          url: item.url,
          attachmentId: item.id
        };
        if (type === 'bill') setBillFiles((prev) => [nextItem, ...prev]);
        if (type === 'invoice') setInvoiceFiles((prev) => [nextItem, ...prev]);
        onSuccess?.(item, file);
      } else {
        onError?.(new Error('upload failed'));
      }
    } catch (e) {
      onError?.(e);
    }
  };

  const removeAttachment = async (file, type) => {
    if (!file?.attachmentId) {
      if (type === 'bill') setBillFiles((prev) => prev.filter((f) => f.uid !== file.uid));
      if (type === 'invoice') setInvoiceFiles((prev) => prev.filter((f) => f.uid !== file.uid));
      return true;
    }
    try {
      await api.delete(`/reimbursements/attachments/${file.attachmentId}`);
      if (type === 'bill') setBillFiles((prev) => prev.filter((f) => f.uid !== file.uid));
      if (type === 'invoice') setInvoiceFiles((prev) => prev.filter((f) => f.uid !== file.uid));
      return true;
    } catch (e) {
      message.error('删除附件失败');
      return false;
    }
  };

  const handlePreview = async (file) => {
    if (isImageFile(file) && (file.url || file.thumbUrl || file.originFileObj)) {
      let src = file.url || file.thumbUrl || (file.originFileObj ? URL.createObjectURL(file.originFileObj) : '');
      if (!src) return;
      // 远程受保护图片：先拉成 blob url，供 antd Image 预览
      if (
        src &&
        !String(src).startsWith('blob:') &&
        !String(src).startsWith('data:') &&
        !/X-Amz-Algorithm=AWS4-HMAC-SHA256/i.test(String(src))
      ) {
        try {
          const blobUrl = await fetchBlobUrl(src);
          src = blobUrl;
        } catch (e) {
          // 拉取失败则回退原地址（可能是公开资源）
        }
      }
      setPreviewImage(src);
      setPreviewOpen(true);
      return;
    }
    if (file?.url) {
      await openWithAuth(file.url);
      return;
    }
    if (file?.originFileObj) {
      const blobUrl = URL.createObjectURL(file.originFileObj);
      window.open(blobUrl, '_blank');
    }
  };

  const addLocalFile = (type) => (file) => {
    const uid = file?.uid || `${Date.now()}_${Math.random()}`;
    const nextItem = {
      uid,
      name: file?.name || '附件',
      status: 'done',
      originFileObj: file,
      thumbUrl: isImageFile(file) ? URL.createObjectURL(file) : undefined
    };
    if (type === 'bill') setBillFiles((prev) => [nextItem, ...(prev || [])]);
    if (type === 'invoice') setInvoiceFiles((prev) => [nextItem, ...(prev || [])]);
    return false;
  };

  const uploadAttachmentForRecord = async (recordId, type, fileItem) => {
    const file = fileItem?.originFileObj;
    if (!recordId || !file) return null;
    const fd = new FormData();
    fd.append('file', file);
    const res = await api.post(`/reimbursements/${recordId}/attachments?type=${type}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    if (res.data?.code !== 0) throw new Error('upload failed');
    const item = res.data.data;
    return {
      uid: String(item.id),
      name: item.file_name,
      status: 'done',
      url: item.url,
      attachmentId: item.id
    };
  };

  const columns = useMemo(() => {
    if (isMobile) {
      return [
        {
          title: '报销信息',
          dataIndex: 'title',
          render: (v, r) => (
            <Space direction="vertical" size={2}>
              <span style={{ fontWeight: 600 }}>{v}</span>
              <span style={{ color: '#666' }}>{r.expense_date ? dayjs(r.expense_date).format('YYYY-MM-DD') : '-'}</span>
              <span>单据 ¥{(Number(r.total_amount || 0)).toFixed(2)} / 已报 ¥{(getDisplayedReimbursedAmount(r)).toFixed(2)} / 剩余 ¥{(Number(r.remaining_amount || 0)).toFixed(2)}</span>
              <Space size={6} wrap>
                <Tag color={(TYPE_OPTIONS.find((item) => item.value === r.reimbursement_type) || {}).color}>{(TYPE_OPTIONS.find((item) => item.value === r.reimbursement_type) || {}).label || r.reimbursement_type || '-'}</Tag>
                <Tag color={(STATUS_OPTIONS.find((item) => item.value === r.status) || {}).color}>{(STATUS_OPTIONS.find((item) => item.value === r.status) || {}).label || r.status || '-'}</Tag>
                <Tag color="blue">单子 {r.bill_count || 0}</Tag>
                <Tag color="purple">发票 {r.invoice_count || 0}</Tag>
              </Space>
              {canEditRecord(r) ? (
                <Space>
                  <Button size="small" onClick={() => openEdit(r)}>编辑</Button>
                  <Button size="small" onClick={() => openPaymentsDrawer(r)}>分次记录</Button>
                  <Button size="small" disabled={Number(r.remaining_amount || 0) <= 0} onClick={() => openPayModal(r)}>申请报销</Button>
                  <Popconfirm title="确认删除这条报销记录？" onConfirm={() => handleDelete(r)}>
                    <Button size="small" danger>删除</Button>
                  </Popconfirm>
                </Space>
              ) : null}
            </Space>
          )
        }
      ];
    }
    const cols = [];
    if (canManage) {
      cols.push({
        title: '报销人',
        dataIndex: 'user_name',
        width: 120,
        render: (v) => v || '-'
      });
    }
    cols.push(
      {
        title: '标题',
        dataIndex: 'title',
        render: (v, r) => (
          <Space direction="vertical" size={0}>
            <span style={{ fontWeight: 600 }}>{v}</span>
            {r.description ? <span style={{ color: '#888', fontSize: 12 }}>{r.description}</span> : null}
          </Space>
        )
      },
      {
        title: '发生日期',
        dataIndex: 'expense_date',
        width: 110,
        render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-')
      },
      {
        title: '单据总额',
        dataIndex: 'total_amount',
        align: 'right',
        width: 120,
        render: (v) => (Number(v || 0)).toFixed(2)
      },
      {
        title: '报销金额',
        dataIndex: 'approved_amount',
        align: 'right',
        width: 120,
        render: (_, r) => (getDisplayedReimbursedAmount(r)).toFixed(2)
      },
      {
        title: '剩余未报',
        dataIndex: 'remaining_amount',
        align: 'right',
        width: 120,
        render: (v) => <span style={{ color: Number(v || 0) > 0 ? '#fa8c16' : '#52c41a' }}>{(Number(v || 0)).toFixed(2)}</span>
      },
      {
        title: '类型',
        dataIndex: 'reimbursement_type',
        width: 100,
        render: (v) => {
          const opt = TYPE_OPTIONS.find((item) => item.value === v);
          return <Tag color={opt?.color || 'default'}>{opt?.label || v || '-'}</Tag>;
        }
      },
      {
        title: '状态',
        dataIndex: 'status',
        width: 100,
        render: (v) => {
          const opt = STATUS_OPTIONS.find((item) => item.value === v);
          return <Tag color={opt?.color || 'default'}>{opt?.label || v || '-'}</Tag>;
        }
      },
      {
        title: '附件',
        width: 140,
        render: (_, r) => (
          <Space size={4} wrap>
            <Tag color="blue">单子 {r.bill_count || 0}</Tag>
            <Tag color="purple">发票 {r.invoice_count || 0}</Tag>
          </Space>
        )
      },
      {
        title: '操作',
        width: 240,
        fixed: 'right',
        render: (_, r) => (
          canEditRecord(r) ? (
            <Space>
              <Button size="small" onClick={() => openEdit(r)}>编辑</Button>
              <Button size="small" onClick={() => openPaymentsDrawer(r)}>分次记录</Button>
              <Button size="small" disabled={Number(r.remaining_amount || 0) <= 0} onClick={() => openPayModal(r)}>申请报销</Button>
              <Button size="small" type="dashed" disabled={String(r.status) === 'rejected'} onClick={() => openPayIn(r)}>报销到账</Button>
              <Popconfirm title="确认删除这条报销记录？" onConfirm={() => handleDelete(r)}>
                <Button size="small" danger>删除</Button>
              </Popconfirm>
            </Space>
          ) : '-'
        )
      }
    );
    return cols;
  }, [canManage, isMobile, user?.id]);

  const mobileModalProps = isMobile ? {
    width: '100%',
    centered: false,
    style: { top: 0, paddingBottom: 0 },
    styles: { body: { maxHeight: 'calc(100vh - 110px)', overflowY: 'auto', padding: 16 } }
  } : {};

  const renderReimbursementCard = (r) => {
    const typeOpt = TYPE_OPTIONS.find((item) => item.value === r.reimbursement_type);
    const statusOpt = STATUS_OPTIONS.find((item) => item.value === r.status);
    const remaining = Number(r.remaining_amount || 0);
    const canApply = remaining > 0;
    return (
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <div style={{ fontWeight: 600, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title || '-'}</div>
            <Tag color={statusOpt?.color || 'default'} style={{ marginRight: 0 }}>{statusOpt?.label || r.status || '-'}</Tag>
          </div>
          <div style={{ color: '#666', fontSize: 12 }}>
            {r.expense_date ? dayjs(r.expense_date).format('YYYY-MM-DD') : '-'}
            {typeOpt ? ` · ${typeOpt.label}` : ''}
          </div>
          <div style={{ fontSize: 13 }}>
            单据 ¥{Number(r.total_amount || 0).toFixed(2)} / 已报 ¥{getDisplayedReimbursedAmount(r).toFixed(2)} / 剩余 ¥{Number(r.remaining_amount || 0).toFixed(2)}
          </div>
          <Space size={6} wrap>
            <Tag color={typeOpt?.color || 'default'} style={{ marginRight: 0 }}>{typeOpt?.label || r.reimbursement_type || '-'}</Tag>
            <Tag color="blue" style={{ marginRight: 0 }}>单子 {r.bill_count || 0}</Tag>
            <Tag color="purple" style={{ marginRight: 0 }}>发票 {r.invoice_count || 0}</Tag>
          </Space>

          {canEditRecord(r) ? (
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <Button block onClick={() => openEdit(r)}>编辑</Button>
              </Col>
              <Col span={12}>
                <Button block onClick={() => openPaymentsDrawer(r)}>分次记录</Button>
              </Col>
              <Col span={12}>
                <Button block disabled={!canApply} onClick={() => openPayModal(r)}>申请报销</Button>
              </Col>
              {canManage ? (
                <Col span={12}>
                  <Button block type="dashed" disabled={String(r.status) === 'rejected'} onClick={() => openPayIn(r)}>报销到账</Button>
                </Col>
              ) : (
                <Col span={12}>
                  <div />
                </Col>
              )}
              <Col span={24}>
                <Popconfirm title="确认删除这条报销记录？" onConfirm={() => handleDelete(r)}>
                  <Button block danger>删除</Button>
                </Popconfirm>
              </Col>
            </Row>
          ) : null}
        </Space>
      </Card>
    );
  };

  const renderPendingPaymentCard = (r) => (
    <Card size="small" style={{ marginBottom: 12 }}>
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
          <div style={{ fontWeight: 600, flex: 1, minWidth: 0 }}>{r.title || '-'}</div>
          <Tag style={{ marginRight: 0 }}>待审批</Tag>
        </div>
        <div style={{ color: '#666', fontSize: 12 }}>申请人：{r.requester_user_name || '-'}</div>
        <div style={{ color: '#666', fontSize: 12 }}>发生日期：{r.expense_date ? dayjs(r.expense_date).format('YYYY-MM-DD') : '-'}</div>
        <div style={{ fontSize: 13 }}>
          单据 ¥{Number(r.total_amount || 0).toFixed(2)} / 已报 ¥{getDisplayedReimbursedAmount(r).toFixed(2)} / 剩余 ¥{Number(r.remaining_amount || 0).toFixed(2)}
        </div>
        <div style={{ fontSize: 13 }}>本次申请：¥{Number(r.requested_amount || 0).toFixed(2)}</div>
        {r.request_note ? <div style={{ color: '#666', fontSize: 12, whiteSpace: 'pre-wrap' }}>备注：{r.request_note}</div> : null}
        <Row gutter={[8, 8]}>
          <Col span={12}>
            <Button block type="primary" onClick={() => openApprove(r)}>通过</Button>
          </Col>
          <Col span={12}>
            <Button block danger onClick={() => openReject(r)}>驳回</Button>
          </Col>
        </Row>
      </Space>
    </Card>
  );

  const renderApprovedMissingCard = (r) => (
    <Card size="small" style={{ marginBottom: 12 }}>
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
          <div style={{ fontWeight: 600, flex: 1, minWidth: 0 }}>{r.title || '-'}</div>
          <Tag color="orange" style={{ marginRight: 0 }}>待补传</Tag>
        </div>
        <div style={{ color: '#666', fontSize: 12 }}>申请人：{r.requester_user_name || '-'}</div>
        <div style={{ color: '#666', fontSize: 12 }}>发生日期：{r.expense_date ? dayjs(r.expense_date).format('YYYY-MM-DD') : '-'}</div>
        <div style={{ fontSize: 13 }}>通过金额：¥{Number(r.approved_amount || 0).toFixed(2)}</div>
        <div style={{ color: '#666', fontSize: 12 }}>通过时间：{r.approved_at ? dayjs(r.approved_at).format('YYYY-MM-DD HH:mm') : '-'}</div>
        <Upload
          showUploadList={false}
          customRequest={uploadApprovalDoc(r.id, async () => {
            await loadApprovedMissingDocs(approvedPagination.current, approvedPagination.pageSize);
          })}
        >
          <Button block>上传审批单</Button>
        </Upload>
      </Space>
    </Card>
  );

  const renderPaymentCard = (p) => {
    const map = { pending: { t: '待审批', c: 'default' }, approved: { t: '已通过', c: 'green' }, rejected: { t: '已驳回', c: 'red' }, cancelled: { t: '已撤销', c: 'default' } };
    const hit = map[String(p?.status)] || { t: p?.status || '-', c: 'default' };
    return (
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
            <Tag color={hit.c} style={{ marginRight: 0 }}>{hit.t}</Tag>
            <div style={{ color: '#666', fontSize: 12 }}>{p?.approved_at ? dayjs(p.approved_at).format('YYYY-MM-DD HH:mm') : ''}</div>
          </div>
          <div style={{ fontSize: 13 }}>申请金额：¥{Number(p?.requested_amount || 0).toFixed(2)}</div>
          <div style={{ fontSize: 13 }}>通过金额：{p?.approved_amount == null ? '—' : `¥${Number(p.approved_amount || 0).toFixed(2)}`}</div>
          {p?.request_note ? <div style={{ color: '#666', fontSize: 12, whiteSpace: 'pre-wrap' }}>申请备注：{p.request_note}</div> : null}
          {p?.approved_by_user_name ? <div style={{ color: '#666', fontSize: 12 }}>审批人：{p.approved_by_user_name}</div> : null}
          {p?.reject_reason ? <div style={{ color: '#ff4d4f', fontSize: 12, whiteSpace: 'pre-wrap' }}>驳回原因：{p.reject_reason}</div> : null}

          <Row gutter={[8, 8]}>
            {p?.approval_doc_url ? (
              <Col span={12}>
                <Button block onClick={() => handlePreview({ url: p.approval_doc_url, name: p.approval_doc_file_name, type: p.approval_doc_mime_type })}>查看审批单</Button>
              </Col>
            ) : (
              <Col span={12}><div /></Col>
            )}

            {String(p?.status) === 'pending' ? (
              <Col span={12}>
                <Popconfirm title="确认撤销这次申请？" onConfirm={() => cancelPayment(p.id)}>
                  <Button block>撤销</Button>
                </Popconfirm>
              </Col>
            ) : (
              <Col span={12}><div /></Col>
            )}

            {(canApprove && String(p?.status) === 'approved' && !p?.approval_doc_url) ? (
              <Col span={24}>
                <Upload
                  showUploadList={false}
                  customRequest={uploadApprovalDoc(p.id, async () => {
                    if (paymentsTarget?.id) await loadPayments(paymentsTarget.id);
                  })}
                >
                  <Button block>上传审批单</Button>
                </Upload>
              </Col>
            ) : null}
          </Row>
        </Space>
      </Card>
    );
  };

  return (
    <div style={{ padding: isMobile ? 12 : 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0, marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? 18 : 20, lineHeight: 1.2 }}>报销管理</h2>
        <Space style={isMobile ? { width: '100%' } : undefined}>
          <Button icon={<ReloadOutlined />} onClick={() => handleSearch()} block={isMobile}>刷新</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} block={isMobile}>新增报销</Button>
        </Space>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Statistic title="报销笔数" value={summary?.total_count || 0} />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic title="单据总额" value={Number(summary?.total_amount || 0).toFixed(2)} prefix="¥" />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic title="已报金额" value={Number(summary?.reimbursed_amount || 0).toFixed(2)} prefix="¥" />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic title="剩余未报" value={Number(summary?.remaining_amount || 0).toFixed(2)} prefix="¥" />
            <div style={{ marginTop: 6 }}>
              <Space size={6} wrap>
                <Tag color="green">已报完 {summary?.full_count || 0}</Tag>
                <Tag color="orange">未报完 {summary?.partial_count || 0}</Tag>
              </Space>
            </div>
          </Col>
        </Row>
      </Card>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'mine',
            label: '我的报销',
            children: (
              <>
                {isMobile ? (
                  <>
                    <Card size="small" style={{ marginBottom: 12 }}>
                      <Space wrap>
                        <Button icon={<FilterOutlined />} onClick={() => setFiltersDrawerOpen(true)}>筛选/查询</Button>
                        <Button onClick={handleReset}>重置</Button>
                      </Space>
                    </Card>
                    <List
                      loading={loading}
                      dataSource={records}
                      renderItem={(r) => <List.Item style={{ padding: 0 }}>{renderReimbursementCard(r)}</List.Item>}
                      pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        simple: true,
                        showSizeChanger: false,
                        onChange: (page, pageSize) => loadRecords(page, pageSize, filters)
                      }}
                    />
                  </>
                ) : (
                  <>
                    <Card style={{ marginBottom: 16 }}>
                      <Row gutter={[12, 12]}>
                        <Col xs={24} sm={12} md={6}>
                          <Input
                            placeholder="搜索标题/说明"
                            value={filters.q}
                            onChange={(e) => setFilters((prev) => ({ ...prev, q: e.target.value }))}
                            allowClear
                          />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <RangePicker
                            style={{ width: '100%' }}
                            value={filters.dateRange}
                            onChange={(dates) => setFilters((prev) => ({ ...prev, dateRange: dates || [] }))}
                          />
                        </Col>
                        <Col xs={24} sm={12} md={4}>
                          <Select
                            placeholder="类型"
                            allowClear
                            style={{ width: '100%' }}
                            value={filters.type}
                            options={TYPE_OPTIONS.map((item) => ({ label: item.label, value: item.value }))}
                            onChange={(value) => setFilters((prev) => ({ ...prev, type: value }))}
                          />
                        </Col>
                        <Col xs={24} sm={12} md={4}>
                          <Select
                            placeholder="状态"
                            allowClear
                            style={{ width: '100%' }}
                            value={filters.status}
                            options={STATUS_OPTIONS.map((item) => ({ label: item.label, value: item.value }))}
                            onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
                          />
                        </Col>
                        <Col xs={24} sm={12} md={4}>
                          <Space wrap>
                            <Button type="primary" onClick={handleSearch}>查询</Button>
                            <Button onClick={handleReset}>重置</Button>
                          </Space>
                        </Col>
                      </Row>
                    </Card>

                    <Card>
                      <Table
                        rowKey="id"
                        columns={columns}
                        dataSource={records}
                        loading={loading}
                        pagination={{
                          current: pagination.current,
                          pageSize: pagination.pageSize,
                          total: pagination.total,
                          onChange: (page, pageSize) => loadRecords(page, pageSize, filters)
                        }}
                        scroll={isMobile ? undefined : { x: 1300 }}
                      />
                    </Card>
                  </>
                )}
              </>
            )
          },
          showApproveTab ? {
            key: 'approve',
            label: '审批管理',
            children: (
              !canApprove ? (
                <Card>
                  <div style={{ color: '#999' }}>当前账号无“项目经理”审批权限（需要权限：project_manager）。</div>
                </Card>
              ) : (
                <Tabs
                  activeKey={approveSubTab}
                  onChange={setApproveSubTab}
                  items={[
                    {
                      key: 'pending',
                      label: `待审批(${pendingPagination.total || 0})`,
                      children: (
                        <Card>
                          {isMobile ? (
                            <List
                              loading={pendingLoading}
                              dataSource={pending}
                              renderItem={(r) => <List.Item style={{ padding: 0 }}>{renderPendingPaymentCard(r)}</List.Item>}
                              pagination={{
                                current: pendingPagination.current,
                                pageSize: pendingPagination.pageSize,
                                total: pendingPagination.total,
                                simple: true,
                                showSizeChanger: false,
                                onChange: (page, pageSize) => loadPending(page, pageSize)
                              }}
                            />
                          ) : (
                            <Table
                              rowKey="id"
                              loading={pendingLoading}
                              dataSource={pending}
                              pagination={{
                                current: pendingPagination.current,
                                pageSize: pendingPagination.pageSize,
                                total: pendingPagination.total,
                                onChange: (page, pageSize) => loadPending(page, pageSize)
                              }}
                              columns={[
                                { title: '申请人', dataIndex: 'requester_user_name', width: 120, render: (v) => v || '-' },
                                { title: '标题', dataIndex: 'title', render: (v, r) => <Space direction="vertical" size={0}><span style={{ fontWeight: 600 }}>{v}</span><span style={{ color: '#888', fontSize: 12 }}>单据¥{Number(r.total_amount || 0).toFixed(2)} / 已报¥{getDisplayedReimbursedAmount(r).toFixed(2)} / 剩余¥{Number(r.remaining_amount || 0).toFixed(2)}</span></Space> },
                                { title: '发生日期', dataIndex: 'expense_date', width: 110, render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-') },
                                { title: '本次申请', dataIndex: 'requested_amount', width: 110, align: 'right', render: (v) => Number(v || 0).toFixed(2) },
                                { title: '备注', dataIndex: 'request_note', width: 160, render: (v) => v || '-' },
                                {
                                  title: '操作',
                                  width: 260,
                                  fixed: 'right',
                                  render: (_, r) => (
                                    <Space wrap>
                                      <Button size="small" type="primary" onClick={() => openApprove(r)}>通过</Button>
                                      <Button size="small" danger onClick={() => openReject(r)}>驳回</Button>
                                    </Space>
                                  )
                                }
                              ]}
                              scroll={isMobile ? undefined : { x: 1100 }}
                            />
                          )}
                        </Card>
                      )
                    },
                    {
                      key: 'approved_missing',
                      label: `待补传审批单(${approvedPagination.total || 0})`,
                      children: (
                        <Card>
                          {isMobile ? (
                            <List
                              loading={approvedLoading}
                              dataSource={approved}
                              renderItem={(r) => <List.Item style={{ padding: 0 }}>{renderApprovedMissingCard(r)}</List.Item>}
                              pagination={{
                                current: approvedPagination.current,
                                pageSize: approvedPagination.pageSize,
                                total: approvedPagination.total,
                                simple: true,
                                showSizeChanger: false,
                                onChange: (page, pageSize) => loadApprovedMissingDocs(page, pageSize)
                              }}
                            />
                          ) : (
                            <Table
                              rowKey="id"
                              loading={approvedLoading}
                              dataSource={approved}
                              pagination={{
                                current: approvedPagination.current,
                                pageSize: approvedPagination.pageSize,
                                total: approvedPagination.total,
                                onChange: (page, pageSize) => loadApprovedMissingDocs(page, pageSize)
                              }}
                              columns={[
                                { title: '申请人', dataIndex: 'requester_user_name', width: 120, render: (v) => v || '-' },
                                { title: '标题', dataIndex: 'title', render: (v, r) => <Space direction="vertical" size={0}><span style={{ fontWeight: 600 }}>{v}</span><span style={{ color: '#888', fontSize: 12 }}>单据¥{Number(r.total_amount || 0).toFixed(2)} / 已批¥{Number(r.approved_amount_sum || 0).toFixed(2)} / 剩余¥{Number(r.remaining_amount || 0).toFixed(2)}</span></Space> },
                                { title: '发生日期', dataIndex: 'expense_date', width: 110, render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-') },
                                { title: '通过金额', dataIndex: 'approved_amount', width: 110, align: 'right', render: (v) => Number(v || 0).toFixed(2) },
                                { title: '通过时间', dataIndex: 'approved_at', width: 160, render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-') },
                                {
                                  title: '操作',
                                  width: 160,
                                  fixed: 'right',
                                  render: (_, r) => (
                                    <Upload
                                      showUploadList={false}
                                      customRequest={uploadApprovalDoc(r.id, async () => {
                                        await loadApprovedMissingDocs(approvedPagination.current, approvedPagination.pageSize);
                                      })}
                                    >
                                      <Button size="small">上传审批单</Button>
                                    </Upload>
                                  )
                                }
                              ]}
                              scroll={isMobile ? undefined : { x: 1200 }}
                            />
                          )}
                        </Card>
                      )
                    }
                  ]}
                />
              )
            )
          } : null
        ].filter(Boolean)}
      />

      <Modal
        open={modalOpen}
        title={editingRecord ? '编辑报销' : '新增报销'}
        onCancel={() => setModalOpen(false)}
        footer={isMobile ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Button block onClick={() => setModalOpen(false)} disabled={saving}>取消</Button>
            <Button block type="primary" onClick={handleSave} loading={saving}>保存</Button>
          </Space>
        ) : ([
          <Button key="cancel" onClick={() => setModalOpen(false)} disabled={saving}>取消</Button>,
          <Button key="ok" type="primary" onClick={handleSave} loading={saving}>保存</Button>
        ])}
        {...mobileModalProps}
        width={isMobile ? '100%' : 780}
      >
        <Form layout="vertical" form={form}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
                <Input placeholder="如：差旅报销" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="expense_date" label="发生日期">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="total_amount" label="单据总额" rules={[{ required: true, message: '请输入单据总额' }]}>
                <Input type="number" min={0} step="0.01" />
              </Form.Item>
            </Col>
            <Form.Item name="reimbursed_amount" hidden>
              <Input />
            </Form.Item>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="reimbursement_type" label="报销类型" rules={[{ required: true, message: '请选择类型' }]}>
                <Select options={TYPE_OPTIONS.map((item) => ({ label: item.label, value: item.value }))} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="status" label="状态" rules={[{ required: true, message: '请选择状态' }]}>
                <Select options={STATUS_OPTIONS.map((item) => ({ label: item.label, value: item.value }))} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="说明">
            <Input.TextArea rows={3} placeholder="补充说明" />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input placeholder="可选" />
          </Form.Item>
        </Form>

        <Divider />

        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>单子附件</div>
            <Upload
              multiple
              fileList={billFiles}
              {...(editingRecord?.id
                ? { customRequest: uploadAttachment('bill') }
                : { beforeUpload: addLocalFile('bill') })}
              onRemove={(file) => removeAttachment(file, 'bill')}
              onPreview={handlePreview}
              listType={isMobile ? 'picture-card' : 'text'}
            >
              {isMobile ? (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>上传</div>
                </div>
              ) : (
                <Button icon={<UploadOutlined />}>上传单子</Button>
              )}
            </Upload>
          </Col>
          <Col xs={24} md={12}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>发票附件</div>
            <Upload
              multiple
              fileList={invoiceFiles}
              {...(editingRecord?.id
                ? { customRequest: uploadAttachment('invoice') }
                : { beforeUpload: addLocalFile('invoice') })}
              onRemove={(file) => removeAttachment(file, 'invoice')}
              onPreview={handlePreview}
              listType={isMobile ? 'picture-card' : 'text'}
            >
              {isMobile ? (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>上传</div>
                </div>
              ) : (
                <Button icon={<UploadOutlined />}>上传发票</Button>
              )}
            </Upload>
          </Col>
        </Row>

        {!editingRecord && (
          <div style={{ marginTop: 12, color: '#999' }}>提示：新增时可先选择附件，点击“保存”后会自动上传。</div>
        )}
      </Modal>

      <Modal
        open={payInModal.open}
        title="报销到账"
        onCancel={() => setPayInModal({ open: false, record: null, saving: false })}
        onOk={submitPayIn}
        confirmLoading={payInModal.saving}
        {...mobileModalProps}
        width={isMobile ? '100%' : 420}
      >
        <div style={{ marginBottom: 8, color: '#666' }}>
          当前已到账：¥{Number(payInModal.record?.reimbursed_amount || 0).toFixed(2)} / 单据总额：¥{Number(payInModal.record?.total_amount || 0).toFixed(2)}
        </div>
        <Form layout="vertical" form={payInForm}>
          <Form.Item
            name="amount"
            label="本次到账金额"
            rules={[
              { required: true, message: '请输入本次到账金额' },
              {
                validator: (_, v) => {
                  const delta = Number(v || 0);
                  if (!(delta > 0)) return Promise.reject(new Error('金额必须大于0'));
                  return Promise.resolve();
                }
              }
            ]}
          >
            <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={payModalOpen}
        title="分次报销申请"
        onCancel={() => { setPayModalOpen(false); setPayTarget(null); }}
        onOk={submitPayRequest}
        confirmLoading={paySaving}
        {...mobileModalProps}
        width={isMobile ? '100%' : 520}
      >
        <div style={{ marginBottom: 8, color: '#666' }}>
          剩余未报：¥{Number(payTarget?.remaining_amount || 0).toFixed(2)}
        </div>
        <Form layout="vertical" form={payForm}>
          <Form.Item
            name="requested_amount"
            label="本次申请金额"
            rules={[
              { required: true, message: '请输入本次申请金额' },
              {
                validator: (_, value) => {
                  const v = Number(value || 0);
                  const max = Number(payTarget?.remaining_amount || 0);
                  if (!(v > 0)) return Promise.reject(new Error('金额必须大于0'));
                  if (v > max) return Promise.reject(new Error(`不能超过剩余未报金额(${max.toFixed(2)})`));
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input type="number" min={0} step="0.01" />
          </Form.Item>
          <Form.Item name="request_note" label="备注">
            <Input.TextArea rows={3} placeholder="可选" />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        open={paymentsDrawerOpen}
        title={paymentsTarget ? `分次记录 - ${paymentsTarget.title}` : '分次记录'}
        onClose={() => { setPaymentsDrawerOpen(false); setPaymentsTarget(null); setPayments([]); }}
        width={isMobile ? '100%' : 720}
      >
        {isMobile ? (
          <List
            loading={paymentsLoading}
            dataSource={payments}
            renderItem={(p) => <List.Item style={{ padding: 0 }}>{renderPaymentCard(p)}</List.Item>}
          />
        ) : (
          <Table
            rowKey="id"
            loading={paymentsLoading}
            dataSource={payments}
            pagination={false}
            columns={[
              { title: '状态', dataIndex: 'status', width: 110, render: (v) => {
                const map = { pending: { t: '待审批', c: 'default' }, approved: { t: '已通过', c: 'green' }, rejected: { t: '已驳回', c: 'red' }, cancelled: { t: '已撤销', c: 'default' } };
                const hit = map[String(v)] || { t: v || '-', c: 'default' };
                return <Tag color={hit.c}>{hit.t}</Tag>;
              }},
              { title: '申请金额', dataIndex: 'requested_amount', align: 'right', width: 110, render: (v) => Number(v || 0).toFixed(2) },
              { title: '通过金额', dataIndex: 'approved_amount', align: 'right', width: 110, render: (v) => (v == null ? '-' : Number(v || 0).toFixed(2)) },
              { title: '申请备注', dataIndex: 'request_note', render: (v) => v || '-' },
              { title: '审批人', dataIndex: 'approved_by_user_name', width: 120, render: (v) => v || '-' },
              { title: '驳回原因', dataIndex: 'reject_reason', width: 160, render: (v) => v || '-' },
              { title: '审批单', width: 140, render: (_, r) => r.approval_doc_url ? (
                <Button size="small" onClick={() => handlePreview({ url: r.approval_doc_url, name: r.approval_doc_file_name, type: r.approval_doc_mime_type })}>查看</Button>
              ) : '-' },
              { title: '操作', width: 160, fixed: 'right', render: (_, r) => (
                <Space wrap>
                  {String(r.status) === 'pending' ? (
                    <Popconfirm title="确认撤销这次申请？" onConfirm={() => cancelPayment(r.id)}>
                      <Button size="small">撤销</Button>
                    </Popconfirm>
                  ) : null}
                  {(canApprove && String(r.status) === 'approved' && !r.approval_doc_url) ? (
                    <Upload
                      showUploadList={false}
                      customRequest={uploadApprovalDoc(r.id, async () => {
                        if (paymentsTarget?.id) await loadPayments(paymentsTarget.id);
                      })}
                    >
                      <Button size="small">上传审批单</Button>
                    </Upload>
                  ) : null}
                </Space>
              )}
            ]}
            scroll={isMobile ? undefined : { x: 1100 }}
          />
        )}
      </Drawer>

      <Drawer
        open={filtersDrawerOpen}
        title="筛选"
        onClose={() => setFiltersDrawerOpen(false)}
        width="100%"
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Input
            placeholder="搜索标题/说明"
            value={filters.q}
            onChange={(e) => setFilters((prev) => ({ ...prev, q: e.target.value }))}
            allowClear
          />
          <RangePicker
            style={{ width: '100%' }}
            value={filters.dateRange}
            onChange={(dates) => setFilters((prev) => ({ ...prev, dateRange: dates || [] }))}
          />
          <Select
            placeholder="类型"
            allowClear
            style={{ width: '100%' }}
            value={filters.type}
            options={TYPE_OPTIONS.map((item) => ({ label: item.label, value: item.value }))}
            onChange={(value) => setFilters((prev) => ({ ...prev, type: value }))}
          />
          <Select
            placeholder="状态"
            allowClear
            style={{ width: '100%' }}
            value={filters.status}
            options={STATUS_OPTIONS.map((item) => ({ label: item.label, value: item.value }))}
            onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
          />
          <Row gutter={[8, 8]}>
            <Col span={12}>
              <Button block onClick={() => { handleReset(); setFiltersDrawerOpen(false); }}>重置</Button>
            </Col>
            <Col span={12}>
              <Button block type="primary" onClick={() => { handleSearch(); setFiltersDrawerOpen(false); }}>查询</Button>
            </Col>
          </Row>
        </Space>
      </Drawer>

      <Modal
        open={approveModal.open}
        title="审批通过"
        onCancel={() => setApproveModal({ open: false, record: null, saving: false })}
        onOk={doApprove}
        confirmLoading={approveModal.saving}
        {...mobileModalProps}
        width={isMobile ? '100%' : 520}
      >
        <Form layout="vertical" form={approveForm}>
          <Form.Item
            name="approved_amount"
            label="审批通过金额"
            rules={[{ required: true, message: '请输入审批通过金额' }]}
          >
            <Input type="number" min={0} step="0.01" />
          </Form.Item>
          <Form.Item name="approve_note" label="审批备注">
            <Input.TextArea rows={3} placeholder="可选" />
          </Form.Item>
        </Form>
        <div style={{ marginTop: 8, color: '#888' }}>
          通过后可上传“当次已审批的报销单”。
        </div>
      </Modal>

      <Modal
        open={rejectModal.open}
        title="驳回申请"
        onCancel={() => setRejectModal({ open: false, record: null, saving: false })}
        onOk={doReject}
        confirmLoading={rejectModal.saving}
        {...mobileModalProps}
        width={isMobile ? '100%' : 520}
      >
        <Form layout="vertical" form={rejectForm}>
          <Form.Item name="reject_reason" label="驳回原因" rules={[{ required: true, message: '请输入驳回原因' }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 审批单上传：放在审批通过后，直接在待审批列表里不展示；由项目经理在通过后进入分次记录里上传更符合流程。 */}
      {/* 这里保留一个隐藏 uploader，供后续扩展在“已通过”列表中上传。 */}
      <Image
        style={{ display: 'none' }}
        preview={{
          visible: previewOpen,
          onVisibleChange: (visible) => setPreviewOpen(visible)
        }}
        src={previewImage}
      />
    </div>
  );
};

export default ReimbursementPage;
